import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";
import AdminGate from "@/components/AdminGate";

const STATUS_COLOR = {
  pending: "#E5C158", confirmed: "#7B906F",
  completed: "#A19E95", cancelled: "#C15C3D",
};

function AdminAppointmentsBody() {
  const [key, setKey] = useState(sessionStorage.getItem("admin_key") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const load = useCallback(async (k) => {
    const useKey = k ?? key;
    if (!useKey) return;
    setLoading(true);
    try {
      const r = await api.listAppointments(useKey);
      setItems(r);
      setAuthed(true);
      sessionStorage.setItem("admin_key", useKey);
    } catch (e) {
      setAuthed(false);
      toast.error(e?.response?.status === 401 ? "Invalid admin key" : "Failed to load");
    }
    setLoading(false);
  }, [key]);
  const setStatus = async (id, status) => {
    try { await api.updateAppointment(id, status, key); await load(); }
    catch (err) { console.error("update failed:", err); toast.error("Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try { await api.deleteAppointment(id, key); await load(); }
    catch (err) { console.error("delete failed:", err); toast.error("Delete failed"); }
  };

  useEffect(() => { if (key) load(key); }, [key, load]);

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">Admin</div>
          <div className="font-heading text-3xl text-[#E6E1D6]">Consultation Requests</div>
          <div className="text-xs text-[#A8896C] font-mono mt-1">Dr. Tejakumar Galla · M.A. Astrology · BHMS</div>
        </div>

        {!authed ? (
          <div className="border border-[#2A2D35] bg-[#181A1F] p-6 max-w-md">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Enter Admin Key</div>
            <Input data-testid="admin-key-input" type="password" value={key}
                   onChange={e => setKey(e.target.value)}
                   className="mt-3 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono"
                   placeholder="Admin passcode" />
            <Button data-testid="admin-key-submit" onClick={() => load()}
                    className="mt-3 rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
              Access
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm text-[#A19E95]">Total: <span className="text-[#D4AF37] font-mono">{items.length}</span></div>
              <Button variant="ghost" onClick={() => load()} disabled={loading}
                      className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent">
                <RefreshCw size={14} className={loading ? "animate-spin mr-2" : "mr-2"} /> Reload
              </Button>
            </div>
            <div className="space-y-3">
              {items.map((a) => (
                <div key={a.appointment_id}
                     className="border border-[#2A2D35] bg-[#181A1F] p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-start"
                     data-testid={`appt-row-${a.appointment_id}`}>
                  <div className="md:col-span-2">
                    <div className="font-heading text-lg text-[#E6E1D6]">{a.name}</div>
                    <div className="text-xs font-mono text-[#A19E95]">{a.phone}</div>
                    {a.email && <div className="text-xs font-mono text-[#6E6B64] truncate">{a.email}</div>}
                  </div>
                  <div className="md:col-span-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#6E6B64]">Preferred</div>
                    <div className="font-mono text-sm text-[#E6E1D6]">{a.preferred_date}</div>
                    <div className="text-xs text-[#A19E95] capitalize">{a.preferred_time}</div>
                  </div>
                  <div className="md:col-span-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#6E6B64]">Type</div>
                    <div className="text-sm text-[#E6E1D6] capitalize">{a.consultation_type}</div>
                  </div>
                  <div className="md:col-span-1">
                    <Select value={a.status} onValueChange={(v) => setStatus(a.appointment_id, v)}>
                      <SelectTrigger data-testid={`appt-status-${a.appointment_id}`}
                                     className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]"
                                     style={{ color: STATUS_COLOR[a.status] }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
                        {["pending","confirmed","completed","cancelled"].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1 flex flex-col gap-2 items-end">
                    <div className="text-[10px] font-mono text-[#6E6B64]">
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                    <button data-testid={`appt-del-${a.appointment_id}`}
                            onClick={() => del(a.appointment_id)}
                            className="text-[#6E6B64] hover:text-[#C15C3D] p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {a.notes && (
                    <div className="md:col-span-6 mt-2 text-xs text-[#A19E95] font-light border-t border-[#2A2D35] pt-2">
                      <span className="text-[10px] uppercase tracking-widest text-[#6E6B64] mr-2">Notes:</span>
                      {a.notes}
                    </div>
                  )}
                  {a.profile_id && (
                    <div className="md:col-span-6 text-[10px] font-mono text-[#D4AF37]">
                      Linked profile: {a.profile_id}
                    </div>
                  )}
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-center text-[#6E6B64] font-mono text-sm py-12">No appointments yet.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminAppointments() {
  return <AdminGate subtitle="Appointment Manager"><AdminAppointmentsBody /></AdminGate>;
}
